import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, ValidatorFn, Validators } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-search-box',
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],
})
export class SearchBoxComponent implements OnInit, OnDestroy {
  @Input() public placeholder = '';
  @Input() public startingValue?: string;
  @Input() public clearAfterSubmit = false;
  @Input() public syncValidators: ValidatorFn[] = [];

  @Input() public onChangeTriggered = false;

  /**
   * Enable search-as-you-type functionality with debouncing.
   * When enabled, search will trigger automatically as the user types
   * with a configurable debounce delay (default 300ms).
   */
  @Input() public searchAsYouType = false;

  /**
   * Debounce time in milliseconds for search-as-you-type functionality.
   * Only used when searchAsYouType is enabled.
   * Default: 300ms
   */
  @Input() public debounceTime = 300;

  /**
   * This event is sent from outside the search box.
   * It will be triggered when we have a new value to search that wasn't triggered using the component itself.
   * This will be mostly used by the changes on the query params.
   * One important thing is from now on the state is not saved anymore when we move from page to page using the menu.
   * If we want the previous stage we could navigate back using the browser
   *
   * One other important thing is that this will trigger when the query params update this component or when the user update this component.
   * That is the reason we make the equality check.
   *
   */
  @Input() public newValueTriggered$?: Observable<string | undefined>;

  @Output() public textOnSelection = new EventEmitter<string | undefined>();

  public searchControl: UntypedFormControl;
  public searchSubmitAttempt: boolean;
  public searchSelected: boolean;

  private _lastSearchValue: string;
  private _newValueSubscription$: Subscription;
  private _searchAsYouTypeSubscription$: Subscription;

  constructor() {
    this.searchSubmitAttempt = false;
    this.searchSelected = false;
  }

  @HostListener('document:keydown.escape', ['$event.target'])
  public onClick() {
    if (this.searchSelected) {
      this.deactivateSearch();
    }
  }

  ngOnInit(): void {
    this.syncValidators.push(Validators.maxLength(1000));
    let formControlValue = '';
    if (this.startingValue) {
      formControlValue = this.startingValue;
      this.searchSubmitAttempt = true;
    }

    this.searchControl = new UntypedFormControl(formControlValue, this.syncValidators);

    // Initialize search-as-you-type if enabled (must be before newValueTriggered$ subscription)
    if (this.searchAsYouType) {
      this._initSearchAsYouType();
    }

    if (this.newValueTriggered$) {
      this._newValueSubscription$ = this.newValueTriggered$.subscribe((searchValue) => {
        if (searchValue) {
          this.searchSubmitAttempt = true;
          // Use setValue instead of creating new FormControl to preserve subscriptions
          this.searchControl.setValue(searchValue, { emitEvent: false });
          this._lastSearchValue = searchValue;
        } else {
          this.searchSubmitAttempt = false;
          this.searchControl.reset();
          this._lastSearchValue = '';
        }
      });
    }
  }

  /**
   * Initialize search-as-you-type functionality with debouncing and distinctUntilChanged.
   * This will automatically trigger search as the user types with the configured debounce delay.
   */
  private _initSearchAsYouType(): void {
    this._searchAsYouTypeSubscription$ = this.searchControl.valueChanges
      .pipe(debounceTime(this.debounceTime), distinctUntilChanged())
      .subscribe((searchValue: string) => {
        const trimmedValue = searchValue?.trim();

        // Only emit if the value has changed from the last search
        if (trimmedValue !== this._lastSearchValue) {
          this._lastSearchValue = trimmedValue || '';

          if (trimmedValue) {
            this.searchSubmitAttempt = true;
            this.textOnSelection.emit(trimmedValue);
          } else {
            // Empty search - clear results
            this.searchSubmitAttempt = false;
            this.textOnSelection.emit(undefined);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this._newValueSubscription$?.unsubscribe();
    this._searchAsYouTypeSubscription$?.unsubscribe();
  }

  public searchFocus() {
    this.searchSelected = true;
  }

  public searchBlur() {
    this.searchSelected = false;
  }

  public activateSearch() {
    const searchValue = this.searchControl.value;
    if (!searchValue && this._lastSearchValue) {
      this.deactivateSearch();
    }

    if (searchValue && this.searchControl.valid && searchValue !== this._lastSearchValue) {
      this._lastSearchValue = searchValue;
      this.textOnSelection.emit(searchValue);
      if (this.clearAfterSubmit) {
        this.searchControl.reset();
        this.searchSubmitAttempt = false;
      } else {
        this.searchSubmitAttempt = true;
      }
    }
  }

  public deactivateSearch(): void {
    this._lastSearchValue = '';
    this.searchControl.reset();
    this.textOnSelection.emit(undefined);

    this.searchSubmitAttempt = false;
  }

  public onChange(): void {
    if (this.onChangeTriggered) {
      this.textOnSelection.emit(this.searchControl.value);
    }
  }
}
